import { s } from '@w/schema'
import { hashString128 } from '@w/utils'
import { ASYNC_VOICE_ID } from './async-voice'
import type { SpeechChunk, SpeechWordTiming } from './page-speech'

/** About 20 seconds of speech, prepared ahead of the playhead. */
const PREPARE_AHEAD_CHARS = 300

export type VoicedChunk = { audio: Uint8Array; words: SpeechWordTiming[] }

export type VoiceChunk = (input: { text: string; languageCode: string | undefined }) => Promise<VoicedChunk>

/** Voiced chunks kept on this device across sessions. */
export type SpeechStore = {
  get: (key: string) => Promise<VoicedChunk | null>
  put: (key: string, chunk: VoicedChunk) => Promise<void>
}

/** Voiced chunks for one listening session: from the device store when voiced
 * before, otherwise from the voice, then kept on the device. Each chunk is
 * requested once per session. */
export function createSpeechSession({ store, voice }: { store: SpeechStore; voice: VoiceChunk }) {
  const pending = new Map<string, Promise<VoicedChunk>>()

  async function fetchChunk(chunk: SpeechChunk, key: string): Promise<VoicedChunk> {
    const stored = await store.get(key).catch(() => null)
    if (stored) return stored
    const voiced = await voice({ text: chunk.text, languageCode: chunk.lang })
    // The device cache is best effort: a full or blocked store never holds back
    // audio that is already voiced (and billed).
    void store.put(key, voiced).catch(() => null)
    return voiced
  }

  function load(chunk: SpeechChunk): Promise<VoicedChunk> {
    const key = chunkKey(chunk)
    let promise = pending.get(key)
    if (!promise) {
      promise = fetchChunk(chunk, key)
      // A failed chunk is requested again on retry.
      promise.catch(() => { pending.delete(key) })
      pending.set(key, promise)
    }
    return promise
  }

  return {
    load,
    /** The chunk if it is on the device or already requested; never voices it. */
    peek(chunk: SpeechChunk): Promise<VoicedChunk | null> {
      const key = chunkKey(chunk)
      return pending.get(key)?.catch(() => null) ?? store.get(key).catch(() => null)
    },
    /** Requests the chunks after `index` until about 20 seconds are prepared. */
    prepareAfter(chunks: SpeechChunk[], index: number) {
      let prepared = 0
      for (const chunk of chunks.slice(index + 1)) {
        if (prepared >= PREPARE_AHEAD_CHARS) break
        // A failed prepare surfaces when the chunk plays, with retry.
        void load(chunk).catch(() => null)
        prepared += chunk.text.length
      }
    },
  }
}

export type SpeechSession = ReturnType<typeof createSpeechSession>

export function chunkKey(chunk: Pick<SpeechChunk, 'text' | 'lang'>) {
  return hashString128(JSON.stringify([ASYNC_VOICE_ID, chunk.lang ?? null, chunk.text]))
}

const wordsSchema = s.array(s.object({ word: s.string(), startMs: s.number(), endMs: s.number() }))
const CACHE_NAME = 'page-speech'

/** Cache Storage: the browser evicts it under storage pressure, and it lives
 * apart from workspace data, so audio is neither replicated nor wiped with a workspace. */
export function cacheStorageSpeechStore(caches: CacheStorage): SpeechStore {
  const url = (key: string) => `https://page-speech.invalid/${key}`
  return {
    async get(key) {
      const response = await (await caches.open(CACHE_NAME)).match(url(key))
      if (!response) return null
      const words = wordsSchema.parse(JSON.parse(decodeURIComponent(response.headers.get('x-words') ?? '[]')))
      return { audio: new Uint8Array(await response.arrayBuffer()), words }
    },
    async put(key, chunk) {
      // Header values are ASCII; word timings carry any script.
      const headers = { 'content-type': 'audio/mpeg', 'x-words': encodeURIComponent(JSON.stringify(chunk.words)) }
      await (await caches.open(CACHE_NAME)).put(url(key), new Response(chunk.audio.slice(), { headers }))
    },
  }
}

/** This device's voiced chunks: Cache Storage where the platform has it. Owned by
 * the router context, one per app. */
export function createDeviceSpeechStore(): SpeechStore {
  return globalThis.caches ? cacheStorageSpeechStore(globalThis.caches) : memorySpeechStore()
}

/** Where Cache Storage does not exist, chunks last for the app's lifetime. */
export function memorySpeechStore(): SpeechStore {
  const chunks = new Map<string, VoicedChunk>()
  return {
    get: key => Promise.resolve(chunks.get(key) ?? null),
    put: (key, chunk) => {
      chunks.set(key, chunk)
      return Promise.resolve()
    },
  }
}
